/**
 * Facebook conversion tracking for the public lead form.
 *
 * A conversion is reported twice on purpose:
 *   1. Browser Pixel  — fast, but blocked by ad blockers and ITP
 *   2. Conversions API — server-side, survives blockers, better match quality
 *
 * Both carry the SAME event_id, so Meta deduplicates them and the conversion is
 * counted once. If either path fails the other still reports it.
 */

/** Read a cookie value, or null when absent. */
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * _fbc is normally set by the Pixel from the fbclid URL param, but only after the
 * Pixel script loads. Build it ourselves when the cookie is not there yet so the
 * click can still be attributed.
 */
function resolveFbc(): string | null {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;

  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid) return null;
  return `fb.1.${Date.now()}.${fbclid}`;
}

export function newEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Report a Lead conversion through both channels.
 * Never throws and never blocks the form — the lead is already saved by now.
 */
export async function trackLeadConversion(
  leadId: string,
  eventId: string,
  apiBaseUrl: string,
  csrfToken: string,
  capiToken: string
): Promise<void> {
  // 1. Browser Pixel — eventID makes Meta dedupe against the CAPI copy
  try {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', { currency: 'EUR', value: 3.0 }, { eventID: eventId });
    }
  } catch {
    // Pixel blocked — CAPI below still reports the conversion
  }

  // 2. Conversions API — server reads the real data from the database
  try {
    await fetch(`${apiBaseUrl}/send-capi-event.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        lead_id: leadId,
        event_id: eventId,
        event_name: 'Lead',
        fbp: readCookie('_fbp'),
        fbc: resolveFbc(),
        source_url: window.location.href,
        capi_token: capiToken,
      }),
    });
  } catch {
    // Tracking must never surface an error to the visitor
  }
}
